/**
 * Fetches the email log for a single thread (`/api/auth/threads/:k/emails`).
 *
 * The email entries are merged into the chat view as synthetic "system"
 * messages by ChatArea so they render inline with the rest of the conversation.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const API = '/api/auth';
const TOKEN_KEY = 'foreman-auth-token';

export interface ThreadEmail {
  id: number;
  sessionKey: string;
  direction: 'in' | 'out';
  fromAddr: string | null;
  toAddr: string | null;
  subject: string | null;
  textBody: string | null;
  messageId: string | null;
  inReplyTo: string | null;
  attachments: Array<{
    filename: string;
    mimeType: string;
    size: number;
    contentBase64?: string;
  }>;
  receivedAt: number;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useThreadEmails(sessionKey: string | null) {
  const [emails, setEmails] = useState<ThreadEmail[]>([]);
  const [loading, setLoading] = useState(false);
  // Tracks the currently-active thread so a slow/late fetch for thread A that
  // resolves AFTER the user switched to B can't land A's emails under B.
  const latestKeyRef = useRef<string | null>(sessionKey);
  useEffect(() => { latestKeyRef.current = sessionKey; }, [sessionKey]);

  const refresh = useCallback(async () => {
    if (!sessionKey) {
      setEmails([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/threads/${encodeURIComponent(sessionKey)}/emails`, {
        headers: authHeaders(),
      });
      // Discard a stale response — the active thread moved on while we awaited.
      if (latestKeyRef.current !== sessionKey) return;
      if (res.ok) {
        const data = await res.json();
        if (latestKeyRef.current !== sessionKey) return;
        setEmails(data.emails || []);
      } else {
        setEmails([]);
      }
    } catch (err) {
      console.error('[useThreadEmails] fetch failed:', err);
      if (latestKeyRef.current === sessionKey) setEmails([]);
    } finally {
      if (latestKeyRef.current === sessionKey) setLoading(false);
    }
  }, [sessionKey]);

  // Clear synchronously when the thread changes so thread A's emails never
  // flash into thread B while B's fetch is still in flight. (refresh() only
  // sets loading — it keeps the old emails until the new response lands.)
  useEffect(() => {
    setEmails([]);
  }, [sessionKey]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Light polling for new inbound emails. Cheap GET on a small table — fine for V1.
  useEffect(() => {
    if (!sessionKey) return;
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [sessionKey, refresh]);

  return { emails, loading, refresh };
}
